import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { TOKEN_KEY } from '../lib/api/auth';
import { authService } from '../service/facets/auth.service';
import { AuthUser } from '../types/auth';

type AuthContextType = {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
};

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const navigate = useNavigate();

  // Vérifier l'état de connexion au chargement
  useEffect(() => {
    const checkAuth = async () => {
      console.log('🔍 [AuthContext] Checking authentication...');
      try {
        const token = localStorage.getItem(TOKEN_KEY);
        console.log(
          '🔑 [AuthContext] Token trouvé:',
          token ? `${token.substring(0, 20)}...` : 'Aucun'
        );

        if (token && token !== 'debug-token' && token.length > 20) {
          try {
            console.log('📡 [AuthContext] Calling API /auth/me...');
            const userData = await authService.getCurrentUser();
            console.log('✅ [AuthContext] User data received:', userData);

            const userToSet: AuthUser = {
              id: String(userData.id),
              name: userData.name,
              email: userData.email,
              avatar:
                userData.photo_url && userData.photo_url.trim() !== ''
                  ? userData.photo_url
                  : undefined,
            };
            console.log('👤 [AuthContext] User to set:', userToSet);
            setUser(userToSet);

            // Stocker les nouvelles données dans le localStorage
            localStorage.setItem('user', JSON.stringify(userToSet));
          } catch (apiError) {
            console.error('❌ [AuthContext] API /auth/me error:', apiError);
            // Fallback : essayer de lire les données du localStorage
            const storedUser = localStorage.getItem('user');
            if (storedUser) {
              console.log(
                '🔄 [AuthContext] Utilisation des données du localStorage comme fallback'
              );
              setUser(JSON.parse(storedUser));
            } else {
              localStorage.removeItem(TOKEN_KEY);
              localStorage.removeItem('user');
              setUser(null);
            }
          }
        } else {
          console.log('❌ [AuthContext] Invalid or missing token');
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem('user');
          setUser(null);
        }
      } catch (error) {
        console.error('❌ [AuthContext] Error during verification:', error);
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem('user');
        setUser(null);
      } finally {
        setIsLoading(false);
        console.log('✅ [AuthContext] Verification completed');
      }
    };

    checkAuth();
  }, []);

  const login = async (email: string, password: string): Promise<boolean> => {
    console.log('🔐 [AuthContext] Tentative de connexion pour:', email);
    setIsLoading(true);

    try {
      const response = await authService.login({ email, password });
      console.log('✅ [AuthContext] Connexion réussie:', response);

      const token = localStorage.getItem(TOKEN_KEY);
      if (token && token.length > 20) {
        // Récupérer les informations complètes de l'utilisateur après le login
        try {
          console.log('📡 [AuthContext] Retrieving user data after login...');
          const userData = await authService.getCurrentUser();
          console.log('✅ [AuthContext] Complete user data:', userData);

          const userToSet: AuthUser = {
            id: String(userData.id),
            name: userData.name,
            email: userData.email,
            avatar:
              userData.photo_url && userData.photo_url.trim() !== ''
                ? userData.photo_url
                : undefined,
          };
          console.log('👤 [AuthContext] Final user to set:', userToSet);
          setUser(userToSet);
          localStorage.setItem('user', JSON.stringify(userToSet));
        } catch (apiError) {
          console.error('❌ [AuthContext] Error retrieving user data:', apiError);
          // Fallback : utiliser les données du login si l'API /me échoue
          const userToSet: AuthUser =
            response.user && response.user.id
              ? {
                  id: String(response.user.id),
                  name: response.user.name || email.split('@')[0],
                  email: response.user.email || email,
                  avatar: undefined,
                }
              : {
                  id: email,
                  email: email,
                  name: email.split('@')[0],
                  avatar: undefined,
                };
          console.log('👤 [AuthContext] Fallback user:', userToSet);
          setUser(userToSet);
          localStorage.setItem('user', JSON.stringify(userToSet));
        }
        return true;
      } else {
        throw new Error('Missing token after login');
      }
    } catch (error) {
      console.error('❌ [AuthContext] Login failed:', error);
      setUser(null);
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem('user');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = useCallback(() => {
    console.log('🚪 [AuthContext] Déconnexion...');
    // 1. Supprimer les données d'authentification
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem('user');

    // 2. Mettre à jour l'état local
    setUser(null);

    // 3. Rediriger vers la page de connexion
    // Utiliser window.location au lieu de navigate pour un rechargement complet
    // Cela garantit que tous les états sont bien réinitialisés
    window.location.href = '/login';
  }, []);

  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
  };

  console.log('🔄 [AuthContext] AuthProvider render - user:', user, 'isAuthenticated:', !!user);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth doit être utilisé à l'intérieur d'un AuthProvider");
  }
  return context;
};
